import { BaseService } from '@services/BaseService';
import {
  ServiceWorkOrderSearchViewModel,
  ServiceWorkOrderViewModel,
  StatusCountViewModel,
  TradeOptionsViewModel,
  SwoPaymentViewModel
} from '@models/ServiceWorkOrder';
import { VendorViewModel } from '@models/Vendor';
import { PageViewModel, OptionsViewModel } from '@models/common.models';
import { SwoAppointmentViewModel } from '@models/SwoAppointment';
import { TripStatusLogViewModel } from '@models/ServiceWorkOrder';
import { InvoiceViewModel } from '@models/Invoice';
import { SwoCviViewModel } from '@models/AuthorizationSwoCvi';
import { PurchaseRequestSimpleViewModel } from '@models/PurchaseRequest';

class SWOService extends BaseService {
  constructor() {
    super('swo');
  }

  getSwoAppointments(id: number): Promise<Array<SwoAppointmentViewModel>> {
    return this.fetch<Array<SwoAppointmentViewModel>>(`${id}/appointments`, {
      method: 'GET'
    });
  }

  getSwoInvoices(id: number): Promise<Array<InvoiceViewModel>> {
    return this.fetch<Array<InvoiceViewModel>>(`${id}/invoices`, {
      method: 'GET'
    });
  }

  getSwo(id: number): Promise<ServiceWorkOrderViewModel> {
    return this.fetch<ServiceWorkOrderViewModel>(`${id}`, {
      method: 'GET'
    });
  }

  resendVendorEmail(swoId: number) {
    return this.fetch(`${swoId}/resend`, {
      method: 'POST'
    });
  }

  saveSWO(swo: ServiceWorkOrderViewModel, linkTicketId?: number) {
    if (swo.id) {
      if (swo.authCode.length > 5) {
        swo.authCode = swo.authCode.substring(0, 5);
      }

      return this.fetch<ServiceWorkOrderViewModel>(`${swo.id}`, {
        method: 'PUT',
        body: JSON.stringify(swo)
      });
    }

    return this.fetch<ServiceWorkOrderViewModel>(linkTicketId !== undefined ? '?ticketId=' + linkTicketId : '', {
      method: 'POST',
      body: JSON.stringify(swo)
    });
  }

  public search(
    pageNumber: number,
    pageSize: number,
    orderBy: string,
    orderByDirection: string,
    query?: string,
    contractId?: number,
    excludeInactiveContracts?: boolean,
    excludeUndispatchableSwos?: boolean,
    ids?: number[],
    priority?: string,
    trade?: string
  ): Promise<PageViewModel<ServiceWorkOrderSearchViewModel>> {
    let queryParameters = [];

    // filters
    if (contractId != null) {
      queryParameters.push(`contractId=${contractId}`);
    }
    if (excludeInactiveContracts != null) {
      queryParameters.push(`excludeInactiveContracts=${excludeInactiveContracts}`);
    }
    if (excludeUndispatchableSwos != null) {
      queryParameters.push(`excludeUndispatchableSwos=${excludeUndispatchableSwos}`);
    }
    if (ids) {
      for (const id of ids) {
        queryParameters.push(`ids=${id}`);
      }
    }
    if (priority) {
      queryParameters.push(`priority=${priority}`);
    }

    if (trade) {
      queryParameters.push(`trade=${trade}`);
    }

    if (query) {
      queryParameters.push(`query=${query}`);
    }

    // paging
    queryParameters.push(`pageNumber=${Math.max(1, pageNumber)}`);
    queryParameters.push(`pageSize=${Math.max(0, pageSize)}`);
    // sorting
    if (orderBy) {
      queryParameters.push(`orderBy=${encodeURIComponent(orderBy)}`);
    }
    if (orderByDirection) {
      queryParameters.push(`orderByDirection=${encodeURIComponent(orderByDirection)}`);
    }

    const uri: string = ''.concat(queryParameters.length > 0 ? '?' : '', queryParameters.join('&'));

    return this.fetch<PageViewModel<ServiceWorkOrderSearchViewModel>>(uri, {
      method: 'GET'
    });
  }

  public getZeroVendorSwos(
    pageNumber: number,
    pageSize: number,
    orderBy: string,
    orderByDirection: string,
    query?: string
  ): Promise<PageViewModel<ServiceWorkOrderSearchViewModel>> {
    let queryParameters = [];
    // paging
    queryParameters.push(`pageNumber=${Math.max(1, pageNumber)}`);
    queryParameters.push(`pageSize=${Math.max(0, pageSize)}`);
    // sorting
    if (orderBy) {
      queryParameters.push(`orderBy=${encodeURIComponent(orderBy)}`);
    }
    if (orderByDirection) {
      queryParameters.push(`orderByDirection=${encodeURIComponent(orderByDirection)}`);
    }

    if (query) {
      queryParameters.push(`query=${query}`);
    }

    const uri: string = 'zero-vendor'.concat(queryParameters.length > 0 ? '?' : '', queryParameters.join('&'));

    return this.fetch<PageViewModel<ServiceWorkOrderSearchViewModel>>(uri, {
      method: 'GET'
    });
  }

  getContract(contractId: number) {
    return this.fetch<ServiceWorkOrderViewModel>(`contract/${contractId}`, {
      method: 'GET'
    });
  }

  getStatuses() {
    return this.fetch<OptionsViewModel[]>('statuses', {
      method: 'GET'
    });
  }

  getTrades(): Promise<TradeOptionsViewModel[]> {
    return this.fetch<TradeOptionsViewModel[]>('trades', {
      method: 'GET'
    });
  }

  getTypes() {
    return this.fetch<OptionsViewModel[]>('types', {
      method: 'GET'
    });
  }

  getCVIs() {
    return this.fetch<OptionsViewModel[]>('cvis', {
      method: 'GET'
    });
  }

  getCallReasons() {
    return this.fetch<OptionsViewModel[]>(`callReasons`, {
      method: 'GET'
    });
  }

  saveSwoPayment(swoId: number, payment: SwoPaymentViewModel) {
    return this.fetch(`${swoId}/payments`, {
      body: JSON.stringify(payment),
      method: 'POST'
    });
  }

  getExclusionTypes() {
    return this.fetch<Array<OptionsViewModel>>('exclusionTypes', {
      method: 'GET'
    });
  }

  getSWODispatchFilterByStatus() {
    return this.fetch('filters', {
      method: 'GET'
    });
  }

  getSWODispatchFilterByTrade() {
    return this.getTrades();
  }

  getVendorsBySwoId(swoId: number): Promise<VendorViewModel[]> {
    return this.fetch<Array<VendorViewModel>>(`${swoId}/vendors`, {
      method: 'GET'
    });
  }

  getStatusCount(): Promise<StatusCountViewModel> {
    return this.fetch('opencount', {
      method: 'GET'
    });
  }

  swoOwnershipChange(id: number, release?: boolean): Promise<ServiceWorkOrderSearchViewModel> {
    if (release) {
      return this.fetch(`${id}/release`, {
        method: 'POST'
      });
    } else {
      return this.fetch(`${id}/own`, {
        method: 'POST'
      });
    }
  }

  assignVendor(id: number, vendorInfo: { id: number; vendor: string }) {
    return this.fetch(`dispatch/${id}`, {
      method: 'POST',
      body: JSON.stringify({ swoId: id, vendorId: vendorInfo.id }) // , vendorTech: vendorInfo.vendor })
    });
  }

  assignVendorOnHold(id: number, vendorInfo: { id: number; vendor: string }) {
    return this.fetch(`${id}/hold`, {
      method: 'POST',
      body: JSON.stringify({ swoId: id, vendorId: vendorInfo.id }) // , vendorTech: vendorInfo.vendor })
    });
  }

  getRedispatchTypes() {
    return this.fetch<Array<OptionsViewModel>>('redispatchTypes', {
      method: 'GET'
    });
  }

  redispatchSWO(
    id: number,
    vendorInfo: { id: number; vendor: string },
    redispatchTypeId: number,
    redispatchComment: string
  ) {
    return this.fetch(`${id}/redispatch`, {
      method: 'POST',
      body: JSON.stringify({
        swoId: id,
        newVendorId: vendorInfo.id,
        redispatchTypeId: redispatchTypeId,
        redispatchComment: redispatchComment
      })
    });
  }

  getTripStatusLog(id: number) {
    return this.fetch<Array<TripStatusLogViewModel>>(`trip/statuslog/${id}`, {
      method: 'GET'
    });
  }

  getSwoCvis(swoId: number, includeWholeSwo: boolean): Promise<SwoCviViewModel[]> {
    return this.fetch<SwoCviViewModel[]>(`${swoId}/cvis?includeWholeSwo=${includeWholeSwo}`, {
      method: 'GET'
    });
  }

  getPurchaseRequests(swoId: number): Promise<PurchaseRequestSimpleViewModel[]> {
    return this.fetch<PurchaseRequestSimpleViewModel[]>(`${swoId}/purchasingRequests`, {
      method: 'GET'
    });
  }
}

export const swoService = new SWOService();



// WEBPACK FOOTER //
// ./src/_services/ServiceWorkOrderService.ts